import pick from 'lodash/pick';
import without from 'lodash/without';

export const any = (Joi: any) => Joi.any();
export const arrayOptional = (Joi: any, items: any) => {
  return Joi.array().items(items);
};
export const arrayRequired = (Joi: any, items: any) => arrayOptional(Joi, items).required();
export const boolean = (Joi: any) => Joi.boolean();
export const dateOptional = (Joi: any) => Joi.date().timestamp();
export const dateRequired = (Joi: any) => dateOptional(Joi).required();
export const enumOptional = (Joi: any, values: Array<string>, label = 'Missing value') =>
  Joi.object().keys({
    name: Joi.valid(...without(values, 'none'))
      .invalid('NONE')
      .messages({
        'any.only': '{{#label}} is required',
        'any.invalid': '{{#label}} is required'
      })
      .label(label),
    ordinal: Joi.number()
  });
export const enumRequired = (Joi: any, values: Array<string>, label) =>
  enumOptional(Joi, values, label).required();
export const emailOptional = (Joi: any) =>
  Joi.string()
    .trim()
    .allow('')
    .email({ tlds: { allow: false } });
export const emailRequired = (Joi: any) => emailOptional(Joi).empty('').required();
export const guidOptional = (Joi: any) => Joi.string().guid();
export const guidRequired = (Joi: any) => guidOptional(Joi).required();
export const labeled = (label, ...args) => `${label || '-'} ${args.join(' ')}`.trim();
export const numberOptional = (Joi: any, min: number = 0, max: number = Number.MAX_VALUE) =>
  Joi.number().min(min).max(max);
export const numberRequired = (Joi: any, min: number = 0, max: number = Number.MAX_VALUE) =>
  numberOptional(Joi, min, max).required();

export const selectionRequired = (
  Joi: any,
  min: number = 0,
  max: number = Number.MAX_VALUE
) => {
  return {
    label: (message) =>
      numberOptional(Joi, min, max)
        .required()
        .messages({
          'number.base': `"${message}" is required`,
          'number.empty': `"${message}" is required`,
          'number.min': `"${message}" is required`,
          'number.max': `"${message}" is required`,
          'number.required': `"${message}" is required`
        })
  };
};

export const phoneCodeOptional = (Joi: any) =>
  Joi.string()
    .trim()
    .allow('')
    .min(1)
    .max(4)
    .pattern(/^\+?[0-9]+$/);
export const phoneCodeRequired = (Joi: any) => phoneCodeOptional(Joi).empty('').required();
export const phoneNumberOptional = (Joi: any) =>
  Joi.string()
    .trim()
    .allow('')
    .min(3)
    .max(12)
    .pattern(/^[0-9]+$/);
export const phoneNumberRequired = (Joi: any) => phoneNumberOptional(Joi).empty('').required();
export const stringOptional = (Joi: any, min: number = 0, max: number = 64) =>
  Joi.string().trim().allow('').min(min).max(max);
export const stringRequired = (Joi: any, min: number = 1, max: number = 64) =>
  stringOptional(Joi, min, max).empty('').required();
export const uriOptional = (Joi: any, scheme: Array = ['http', 'https']) =>
  Joi.string().trim().allow('').uri({ scheme });
export const uriRequired = (Joi: any, scheme: Array = ['http', 'https']) =>
  uriOptional(Joi, scheme).empty('').required();
export const timeStampEpochOptional = (Joi: any) => Joi.number().integer();

export const timeStampEpochRequired = (Joi: any) =>
  timeStampEpochOptional(Joi).empty(0).required();

export const custom = (Joi) =>
  Joi.extend((joi) => {
    return {
      type: 'object',
      base: joi.object(),
      messages: {
        orValue: '{{#label}} at least one option must be selected'
      },
      rules: {
        orValue: {
          multi: true, // Rule supports multiple invocations
          method(...q) {
            return this.$_addRule({ name: 'orValue', args: { q } });
          },
          args: [
            {
              name: 'q',
              ref: true,
              assert: (value) => typeof value === 'object',
              message: 'must be object'
            }
          ],
          validate(value, helpers, args) {
            const valid = Object.keys(pick(value, args.q)).some((key) => value[key]);

            if (valid) {
              return value; // Value is valid
            }

            return helpers.error(
              'orValue',
              { q: args.q },
              null,
              Object.assign(helpers.state, {
                path: helpers.state.path.concat('*') // must do this because of UI
              })
            );
          }
        }
      }
    };
  });

export default {
  any,
  arrayOptional,
  arrayRequired,
  boolean,
  enumOptional,
  enumRequired,
  emailOptional,
  emailRequired,
  guidOptional,
  guidRequired,
  numberOptional,
  numberRequired,
  stringOptional,
  stringRequired,
  uriOptional,
  uriRequired,
  timeStampEpochOptional,
  timeStampEpochRequired,
  custom
};
